// A tiny, dependency-free unified/rehype plugin that linkifies bare URLs
// (`https://…` / `http://…`) in text nodes of the hast tree.
//
// Why this exists: `components/Markdown.tsx` only enables `remark-gfm` for the
// chat variants, so GFM's autolink-literal extension turns bare URLs into
// `<a>` there. The `compact` variant (pulse bodies, knowledge entries) runs
// with no remark plugins by design (we don't want GFM tables/strikethrough),
// so a bare URL renders as inert text. Applying this rehype plugin to BOTH
// variants fixes compact and is a no-op for chat — remark-gfm has already
// wrapped those URLs in `<a>` by the hast stage, and we skip `<a>` subtrees.
//
// Deliberately NO dependency on `unist-util-visit` — we recurse manually so
// we can splice replacement nodes into each parent's `children` array.

import { linkifySegments } from './linkify';

// Minimal local hast shapes (avoid pulling in @types/hast and avoid `any`).
interface HastText {
  type: 'text';
  value: string;
}

interface HastElement {
  type: 'element';
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}

interface HastParent {
  // `root` and any other container node — we only rely on `children`.
  type: string;
  tagName?: string;
  children?: HastNode[];
}

type HastNode = HastText | HastElement | HastParent;

// Elements whose subtrees we must not touch: existing anchors (real
// `[text](url)` links + gfm-autolinked URLs) and code/pre (URLs there stay
// literal). Skipping `<a>` is also what makes the plugin idempotent.
const SKIP_TAGS = new Set(['a', 'code', 'pre']);

function isElement(node: HastNode): node is HastElement {
  return node.type === 'element';
}

function isText(node: HastNode): node is HastText {
  return node.type === 'text';
}

// Build the replacement sequence for a single text node's value. Returns the
// original node (unwrapped) when no bare URL is present, so unchanged text
// nodes stay byte-identical.
function linkifyText(value: string): HastNode[] {
  const segments = linkifySegments(value);
  return segments.map((s): HastNode =>
    s.kind === 'url'
      ? {
          type: 'element',
          tagName: 'a',
          properties: { href: s.value },
          children: [{ type: 'text', value: s.value }],
        }
      : { type: 'text', value: s.value },
  );
}

function transformChildren(children: HastNode[]): HastNode[] {
  const next: HastNode[] = [];
  for (const child of children) {
    if (isText(child)) {
      const replacement = linkifyText(child.value);
      for (const node of replacement) next.push(node);
      continue;
    }
    if (isElement(child)) {
      if (SKIP_TAGS.has(child.tagName)) {
        // Do not recurse or linkify inside anchors/code/pre.
        next.push(child);
        continue;
      }
      if (Array.isArray(child.children)) {
        child.children = transformChildren(child.children);
      }
      next.push(child);
      continue;
    }
    // Any other container node (e.g. root nested oddly) — recurse if it has
    // children, otherwise pass through untouched.
    const parent = child as HastParent;
    if (Array.isArray(parent.children)) {
      parent.children = transformChildren(parent.children);
    }
    next.push(child);
  }
  return next;
}

export default function rehypeBareAutolink() {
  return (tree: HastParent): void => {
    if (Array.isArray(tree.children)) {
      tree.children = transformChildren(tree.children);
    }
  };
}
